/**
 * Locations hooks using React Relay
 *
 * These hooks provide access to location data through Relay's
 * normalized cache and automatic data management.
 *
 * Note: Run `npm run relay` after making changes to GraphQL queries
 * to regenerate the type artifacts.
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  useLazyLoadQuery,
  usePaginationFragment,
  useQueryLoader,
  usePreloadedQuery,
  PreloadedQuery,
  fetchQuery,
} from 'react-relay';
import graphql from 'babel-plugin-relay/macro';
import { relayEnvironment } from '../../../../relay';
import type {
  useLocationsRelayQuery as UseLocationsRelayQuery,
  useLocationsRelayQuery$variables,
  LocationFilterInput,
  LocationSortInput,
} from './__generated__/useLocationsRelayQuery.graphql';
import type { useLocationsRelayDirectQuery as UseLocationsRelayDirectQuery } from './__generated__/useLocationsRelayDirectQuery.graphql';
import type { useLocationsRelayByIdQuery } from './__generated__/useLocationsRelayByIdQuery.graphql';
import type { useLocationsRelayPaginationFragment$key } from './__generated__/useLocationsRelayPaginationFragment.graphql';

// ============================================================================
// GraphQL Definitions
// ============================================================================

/**
 * Pagination fragment for locations list
 */
const LocationsListPaginationFragment = graphql`
  fragment useLocationsRelayPaginationFragment on RootQueryType
  @refetchable(queryName: "useLocationsRelayPaginationQuery")
  @argumentDefinitions(
    first: { type: "Int", defaultValue: 100 }
    after: { type: "String" }
    filter: { type: "LocationFilterInput" }
    sort: { type: "[LocationSortInput!]" }
  ) {
    listLocations(first: $first, after: $after, filter: $filter, sort: $sort)
      @connection(
        key: "useLocationsRelayPaginationFragment_listLocations"
        filters: ["filter", "sort"]
      ) {
      count
      pageInfo {
        hasNextPage
        hasPreviousPage
        startCursor
        endCursor
      }
      edges {
        cursor
        node {
          id
          oldId
          name
          state
          configuration
          lat
          lng
          polygonCoordinates
          capacity
          radius
          zoneType
          slowZoneSpeed
          reservations
          maxReservations
          preventEndingRentals
          preventStartingRentals
          private
          module
          bannerId
          iconId
          availableHardwareCount
          totalHardwareCount
          totalRentableHardwareCount
          rentalsStartedCount
          rentalsEndedCount
          avgRentalsStartedPerUser
          avgRentalsEndedPerUser
          system {
            id
            oldId
            name
            timezone
          }
          icon {
            id
            name
            type
            approvedOn
          }
          rates {
            id
            oldId
            name
            baseRate
            baseRateIncludedTime
            freeTime
            pricePerPeriod
            periodDuration
            maxPeriods
            creditCardHold
            minimumWalletDeposit
            membershipRequired
            hidden
            archivedOn
          }
        }
      }
    }
  }
`;

/**
 * Query for paginated locations list (uses fragment for Suspense hooks)
 */
const LocationsListQuery = graphql`
  query useLocationsRelayQuery(
    $first: Int
    $after: String
    $filter: LocationFilterInput
    $sort: [LocationSortInput!]
  ) {
    ...useLocationsRelayPaginationFragment
      @arguments(first: $first, after: $after, filter: $filter, sort: $sort)
  }
`;

/**
 * Direct query for locations list (for non-Suspense fetchQuery usage)
 * This query doesn't use fragments so fetchQuery returns the actual data
 */
const LocationsListDirectQuery = graphql`
  query useLocationsRelayDirectQuery(
    $first: Int
    $after: String
    $filter: LocationFilterInput
    $sort: [LocationSortInput!]
  ) {
    listLocations(first: $first, after: $after, filter: $filter, sort: $sort) {
      count
      pageInfo {
        hasNextPage
        hasPreviousPage
        startCursor
        endCursor
      }
      edges {
        cursor
        node {
          id
          oldId
          name
          state
          configuration
          lat
          lng
          polygonCoordinates
          capacity
          radius
          zoneType
          slowZoneSpeed
          reservations
          maxReservations
          preventEndingRentals
          preventStartingRentals
          private
          module
          bannerId
          iconId
          availableHardwareCount
          totalHardwareCount
          totalRentableHardwareCount
          rentalsStartedCount
          rentalsEndedCount
          avgRentalsStartedPerUser
          avgRentalsEndedPerUser
          system {
            id
            oldId
            name
            timezone
          }
          icon {
            id
            name
            type
            approvedOn
          }
          rates {
            id
            oldId
            name
            baseRate
            baseRateIncludedTime
            freeTime
            pricePerPeriod
            periodDuration
            maxPeriods
            creditCardHold
            minimumWalletDeposit
            membershipRequired
            hidden
            archivedOn
          }
        }
      }
    }
  }
`;

/**
 * Query for single location by ID
 */
const LocationByIdQuery = graphql`
  query useLocationsRelayByIdQuery($id: ID!) {
    getLocation(id: $id) {
      id
      oldId
      name
      state
      configuration
      lat
      lng
      polygonCoordinates
      capacity
      radius
      zoneType
      slowZoneSpeed
      reservations
      maxReservations
      preventEndingRentals
      preventStartingRentals
      private
      module
      bannerId
      iconId
      availableHardwareCount
      totalHardwareCount
      totalRentableHardwareCount
      rentalsStartedCount
      rentalsEndedCount
      avgRentalsStartedPerUser
      avgRentalsEndedPerUser
      system {
        id
        oldId
        name
        timezone
      }
      icon {
        id
        name
        type
        approvedOn
      }
      rates {
        id
        oldId
        name
        baseRate
        baseRateIncludedTime
        freeTime
        pricePerPeriod
        periodDuration
        maxPeriods
        creditCardHold
        minimumWalletDeposit
        membershipRequired
        hidden
        archivedOn
      }
    }
  }
`;

// ============================================================================
// Types
// ============================================================================

export type { LocationFilterInput, LocationSortInput };

/**
 * System type from GraphQL (nested in Location)
 */
export interface LocationSystemGraphQL {
  id: string;
  oldId?: string | null;
  name: string;
  timezone?: string | null;
}

/**
 * Icon/Image type from GraphQL
 */
export interface LocationIconGraphQL {
  id: string;
  name?: string | null;
  type?: string | null;
  approvedOn?: string | null;
}

/**
 * Rate type from GraphQL
 */
export interface LocationRateGraphQL {
  id: string;
  oldId?: string | null;
  name: string;
  baseRate?: number | null;
  baseRateIncludedTime?: number | null;
  freeTime?: number | null;
  pricePerPeriod?: number | null;
  periodDuration?: number | null;
  maxPeriods?: number | null;
  creditCardHold?: number | null;
  minimumWalletDeposit?: number | null;
  membershipRequired?: boolean | null;
  hidden?: boolean | null;
  archivedOn?: string | null;
}

/**
 * Location type from GraphQL - matches the fragment fields
 */
export interface LocationGraphQL {
  id: string;
  oldId?: string | null;
  name: string;
  state?: number | null;
  configuration?: string | null;
  lat?: number | null;
  lng?: number | null;
  polygonCoordinates?: ReadonlyArray<ReadonlyArray<number>> | null;
  capacity?: number | null;
  radius?: number | null;
  zoneType?: string | null;
  slowZoneSpeed?: number | null;
  reservations?: boolean | null;
  maxReservations?: number | null;
  preventEndingRentals?: boolean | null;
  preventStartingRentals?: boolean | null;
  private?: boolean | null;
  module?: string | null;
  bannerId?: string | null;
  iconId?: string | null;
  availableHardwareCount: number;
  totalHardwareCount: number;
  totalRentableHardwareCount: number;
  rentalsStartedCount: number;
  rentalsEndedCount: number;
  avgRentalsStartedPerUser?: number | null;
  avgRentalsEndedPerUser?: number | null;
  system?: LocationSystemGraphQL | null;
  icon?: LocationIconGraphQL | null;
  rates?: LocationRateGraphQL[] | null;
}

/**
 * Simplified filter options for common use cases
 */
export interface LocationQueryFilter {
  systemId?: string;
  systemIds?: string[]; // Filter by multiple systems (mapped to systemId `in`)
  systemOldId?: string;
  organizationId?: string;
  state?: number;
  configuration?: string;
  name?: string;
  private?: boolean;
  zoneType?: string;
}

// ============================================================================
// Hooks
// ============================================================================

/**
 * Hook for fetching locations list using Relay
 *
 * This hook uses Relay's useLazyLoadQuery which suspends while loading.
 * Wrap with Suspense boundary.
 *
 * @example
 * function LocationList() {
 *   const { data, loadNext, hasNext, isLoadingNext } = useLocationsRelay({
 *     systemId: 'uuid',
 *   });
 *
 *   return (
 *     <>
 *       {data.map(location => <div key={location.id}>{location.name}</div>)}
 *       {hasNext && <button onClick={() => loadNext(100)}>Load More</button>}
 *     </>
 *   );
 * }
 *
 * // With Suspense
 * <Suspense fallback={<Loading />}>
 *   <LocationList />
 * </Suspense>
 */
export function useLocationsRelay(
  filter?: LocationQueryFilter,
  options?: {
    limit?: number;
    sort?: LocationSortInput[];
  }
) {
  const limit = options?.limit ?? 100;
  const sort = options?.sort ?? [{ field: 'NAME', order: 'ASC' }];

  const variables: useLocationsRelayQuery$variables = {
    first: limit,
    filter: buildRelayFilter(filter),
    sort,
  };

  // Initial query - suspends while loading
  const queryData = useLazyLoadQuery<UseLocationsRelayQuery>(
    LocationsListQuery,
    variables
  );

  // Pagination fragment for load more
  const { data, loadNext, hasNext, isLoadingNext, refetch } = usePaginationFragment<
    UseLocationsRelayQuery,
    useLocationsRelayPaginationFragment$key
  >(LocationsListPaginationFragment, queryData);

  // Extract nodes from edges
  const nodes =
    data.listLocations?.edges?.map((edge) => edge?.node).filter(Boolean) ?? [];

  return {
    data: nodes as LocationGraphQL[],
    count: data.listLocations?.count,
    pageInfo: data.listLocations?.pageInfo,
    loadNext,
    hasNext,
    isLoadingNext,
    refetch,
  };
}

/**
 * Hook for fetching a single location by ID using Relay
 *
 * Uses Suspense - wrap with Suspense boundary
 *
 * @example
 * function LocationDetail({ id }: { id: string }) {
 *   const location = useLocationByIdRelay(id);
 *   return <div>{location?.name}</div>;
 * }
 */
export function useLocationByIdRelay(locationId: string) {
  const data = useLazyLoadQuery<useLocationsRelayByIdQuery>(LocationByIdQuery, {
    id: locationId,
  });

  return data.getLocation as LocationGraphQL | null;
}

// ============================================================================
// Non-Suspense Hooks (with loading state)
// ============================================================================

/**
 * Hook that returns a query loader for locations list
 *
 * This provides explicit loading state without requiring Suspense.
 * Call `loadQuery` to start fetching, then pass `queryRef` to a child component.
 *
 * @example
 * function LocationContainer() {
 *   const { queryRef, loadQuery, isLoading } = useLocationsRelayLoader();
 *
 *   useEffect(() => {
 *     loadQuery({ systemId: 'uuid' });
 *   }, []);
 *
 *   if (isLoading || !queryRef) {
 *     return <Loading />;
 *   }
 *
 *   return <LocationListWithData queryRef={queryRef} />;
 * }
 *
 * function LocationListWithData({ queryRef }) {
 *   const data = useLocationsRelayPreloaded(queryRef);
 *   return <div>{data.map(l => l.name)}</div>;
 * }
 */
export function useLocationsRelayLoader(initialFilter?: LocationQueryFilter) {
  const [queryRef, loadQuery, disposeQuery] = useQueryLoader<UseLocationsRelayQuery>(
    LocationsListQuery,
    initialFilter
      ? {
          first: 100,
          filter: buildRelayFilter(initialFilter),
          sort: [{ field: 'NAME', order: 'ASC' }],
        }
      : undefined
  );

  const load = (
    filter?: LocationQueryFilter,
    options?: { limit?: number; sort?: LocationSortInput[] }
  ) => {
    const limit = options?.limit ?? 100;
    const sort = options?.sort ?? [{ field: 'NAME' as const, order: 'ASC' as const }];

    loadQuery({
      first: limit,
      filter: buildRelayFilter(filter),
      sort,
    });
  };

  return {
    queryRef,
    loadQuery: load,
    disposeQuery,
    isLoading: queryRef === null,
  };
}

/**
 * Hook to read data from a preloaded locations query
 *
 * Use with useLocationsRelayLoader. Still suspends, but the data
 * is already loading so suspension is minimal.
 */
export function useLocationsRelayPreloaded(
  queryRef: PreloadedQuery<UseLocationsRelayQuery>
) {
  const queryData = usePreloadedQuery<UseLocationsRelayQuery>(
    LocationsListQuery,
    queryRef
  );

  const { data, loadNext, hasNext, isLoadingNext, refetch } = usePaginationFragment<
    UseLocationsRelayQuery,
    useLocationsRelayPaginationFragment$key
  >(LocationsListPaginationFragment, queryData);

  const nodes =
    data.listLocations?.edges?.map((edge) => edge?.node).filter(Boolean) ?? [];

  return {
    data: nodes as LocationGraphQL[],
    count: data.listLocations?.count,
    pageInfo: data.listLocations?.pageInfo,
    loadNext,
    hasNext,
    isLoadingNext,
    refetch,
  };
}

/**
 * Hook for location by ID with explicit loading state (no Suspense required)
 *
 * @example
 * function LocationDetail({ id }: { id: string }) {
 *   const { queryRef, loadQuery, isLoading } = useLocationByIdRelayLoader();
 *
 *   useEffect(() => {
 *     loadQuery(id);
 *   }, [id]);
 *
 *   if (isLoading || !queryRef) return <Loading />;
 *
 *   return <LocationDetailContent queryRef={queryRef} />;
 * }
 */
export function useLocationByIdRelayLoader(initialId?: string) {
  const [queryRef, loadQuery, disposeQuery] = useQueryLoader<useLocationsRelayByIdQuery>(
    LocationByIdQuery,
    initialId ? { id: initialId } : undefined
  );

  const load = (locationId: string) => {
    loadQuery({ id: locationId });
  };

  return {
    queryRef,
    loadQuery: load,
    disposeQuery,
    isLoading: queryRef === null,
  };
}

/**
 * Hook to read data from a preloaded location-by-id query
 */
export function useLocationByIdRelayPreloaded(
  queryRef: PreloadedQuery<useLocationsRelayByIdQuery>
) {
  const data = usePreloadedQuery<useLocationsRelayByIdQuery>(LocationByIdQuery, queryRef);
  return data.getLocation as LocationGraphQL | null;
}

// ============================================================================
// Helpers
// ============================================================================

/**
 * Builds a Relay/GraphQL LocationFilterInput from the simplified filter interface
 */
function buildRelayFilter(filter?: LocationQueryFilter): LocationFilterInput | undefined {
  if (!filter) return undefined;

  const relayFilter: LocationFilterInput = {};

  if (filter.systemIds && filter.systemIds.length > 0) {
    relayFilter.systemId = { in: filter.systemIds };
  } else if (filter.systemId) {
    relayFilter.systemId = { eq: filter.systemId };
  }
  if (filter.systemOldId) {
    relayFilter.systemOldId = { eq: filter.systemOldId };
  }
  if (filter.organizationId) {
    relayFilter.organizationId = { eq: filter.organizationId };
  }
  if (filter.state !== undefined) {
    relayFilter.state = { eq: filter.state };
  }
  if (filter.configuration) {
    relayFilter.configuration = { eq: filter.configuration };
  }
  if (filter.name) {
    // Support pattern matching for name - if it contains % use ilike, otherwise eq
    if (filter.name.includes('%')) {
      relayFilter.name = { ilike: filter.name };
    } else {
      relayFilter.name = { ilike: `%${filter.name}%` };
    }
  }
  if (filter.private !== undefined) {
    relayFilter.private = { eq: filter.private };
  }
  if (filter.zoneType) {
    relayFilter.zoneType = { eq: filter.zoneType };
  }

  return Object.keys(relayFilter).length > 0 ? relayFilter : undefined;
}

// ============================================================================
// React Query-like Interface (No Suspense)
// ============================================================================

/**
 * Hook that provides React Query-like interface for locations data
 *
 * This hook does NOT use Suspense - it provides isLoading, data, refetch etc.
 * similar to React Query's useQuery.
 *
 * @example
 * const { data, isLoading, refetch, isFetched } = useLocationsRelayQuery({
 *   systemId: 'uuid',
 * });
 *
 * if (isLoading) return <Loading />;
 * return <div>{data?.map(l => l.name)}</div>;
 */
export function useLocationsRelayQuery(
  filter?: LocationQueryFilter,
  options?: {
    limit?: number;
    after?: string | null;
    sort?: LocationSortInput[];
    enabled?: boolean;
  }
) {
  const [data, setData] = useState<LocationGraphQL[] | null>(null);
  const [count, setCount] = useState<number | undefined>(undefined);
  const [hasMore, setHasMore] = useState(false);
  const [endCursor, setEndCursor] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isFetched, setIsFetched] = useState(false);
  const [isRefetching, setIsRefetching] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [fetchCount, setFetchCount] = useState(0); // For manual refetch trigger

  const limit = options?.limit ?? 100;
  const after = options?.after ?? undefined;
  const sort = useMemo(
    () => options?.sort ?? [{ field: 'NAME' as const, order: 'ASC' as const }],
    [options?.sort]
  );
  const enabled = options?.enabled !== false;

  // Serialize filter to stable string for dependency comparison
  const filterKey = useMemo(() => JSON.stringify(filter), [filter]);

  useEffect(() => {
    if (!enabled) {
      setIsLoading(false);
      return;
    }

    let cancelled = false;

    const doFetch = async () => {
      try {
        if (isFetched) {
          setIsRefetching(true);
        } else {
          setIsLoading(true);
        }

        const variables: useLocationsRelayQuery$variables = {
          first: limit,
          after,
          filter: buildRelayFilter(filter),
          sort,
        };

        // Use direct query (not fragment-based) for fetchQuery compatibility
        const result = await fetchQuery<UseLocationsRelayDirectQuery>(
          relayEnvironment,
          LocationsListDirectQuery,
          variables
        ).toPromise();

        if (cancelled) return;

        if (result) {
          const nodes =
            result.listLocations?.edges?.map((edge) => edge?.node).filter(Boolean) ?? [];
          setData(nodes as LocationGraphQL[]);
          setCount(result.listLocations?.count ?? undefined);
          setHasMore(result.listLocations?.pageInfo?.hasNextPage ?? false);
          setEndCursor(result.listLocations?.pageInfo?.endCursor ?? null);
          setError(null);
        }
      } catch (err) {
        if (cancelled) return;
        setError(err instanceof Error ? err : new Error('Failed to fetch locations'));
      } finally {
        if (!cancelled) {
          setIsLoading(false);
          setIsFetched(true);
          setIsRefetching(false);
        }
      }
    };

    doFetch();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, filterKey, limit, after, sort, fetchCount]);

  const refetch = useCallback(() => {
    setFetchCount((c) => c + 1);
  }, []);

  return {
    data,
    count,
    hasMore,
    endCursor,
    isLoading,
    isFetched,
    isRefetching,
    error,
    refetch,
  };
}
